import {gql} from '@apollo/client';

const MealPlanFragment = gql`
    fragment MealPlanFragment on MealPlan {
        id
        name
        startDate
        endDate
        status
        recurring
        groups {
            id
            title
            profileImage {
                url
            }
        }
        children {
            id
            name {
                fullName
            }
            profileImage {
                url
            }
        }
        tags {
            id
            title
            profileImage {
                url
            }
        }
        catchAll
    }
`;

export const MealPlans = gql`
    query MealPlans($siteSetId: InstitutionSetId!, $status: [MealPlanStatus!]) {
        mealPlans {
            list(siteSetId: $siteSetId, status: $status) {
                ...MealPlanFragment
            }
        }
    }
    ${MealPlanFragment}
`;

const MealPlanScheduleFragment = gql`
    fragment MealPlanScheduleFragment on MealPlanSchedule {
        currentWeek
        days {
            day
            week
            meals {
                foodItems {
                    id
                    title
                }
                mealType {
                    id
                    title
                }
            }
        }
    }
`;

export const MealPlanSchedule = gql`
    query MealPlanSchedule($mealPlanId: MealPlanId!, $startDate: LocalDate, $nWeeks: Int) {
        mealPlans {
            mealPlanSchedule(mealPlanId: $mealPlanId, startDate: $startDate, nWeeks: $nWeeks) {
                ...MealPlanScheduleFragment
            }
        }
    }
    ${MealPlanScheduleFragment}
`;

export const GetMealPlan = gql`
    query GetMealPlan($mealPlanId: MealPlanId!) {
        mealPlans {
            mealPlanSchedule(mealPlanId: $mealPlanId) {
                mealPlan {
                    ...MealPlanFragment
                }
            }
        }
    }
    ${MealPlanFragment}
`;

export const CreateMealPlan = gql`
    mutation CreateMealPlan(
        $siteSetId: InstitutionSetId!
        $catchAll: Boolean!
        $name: String
        $groupIds: [GroupId!]!
        $childIds: [ChildId!]!
        $tagIds: [TagId!]!
        $recurring: Boolean!
        $startDate: LocalDate
        $endDate: LocalDate
        $mealPlanDays: [DayMealPlanInput!]!
    ) {
        mealPlans {
            create(
                siteSetId: $siteSetId
                catchAll: $catchAll
                name: $name
                groupIds: $groupIds
                childIds: $childIds
                tagIds: $tagIds
                recurring: $recurring
                startDate: $startDate
                endDate: $endDate
                mealPlanDays: $mealPlanDays
            ) {
                ...MealPlanFragment
            }
        }
    }
    ${MealPlanFragment}
`;

export const UpdateMealPlan = gql`
    mutation UpdateMealPlan(
        $mealPlanId: MealPlanId!
        $catchAll: Boolean!
        $name: String
        $groupIds: [GroupId!]!
        $childIds: [ChildId!]!
        $tagIds: [TagId!]!
        $recurring: Boolean
        $dates: MealPlanUpdateDatesInput
        $mealPlanDays: [DayMealPlanInput!]
    ) {
        mealPlans {
            update(
                mealPlanId: $mealPlanId
                catchAll: $catchAll
                name: $name
                groupIds: $groupIds
                childIds: $childIds
                tagIds: $tagIds
                recurring: $recurring
                dates: $dates
                mealPlanDays: $mealPlanDays
            ) {
                ...MealPlanFragment
            }
        }
    }
    ${MealPlanFragment}
`;

export const PublishMealPlan = gql`
    mutation PublishMealPlan($mealPlanId: MealPlanId!) {
        mealPlans {
            publish(mealPlanId: $mealPlanId) {
                ...MealPlanFragment
            }
        }
    }
    ${MealPlanFragment}
`;

export const DeleteMealPlan = gql`
    mutation DeleteMealPlan($mealPlanId: MealPlanId!) {
        mealPlans {
            delete(mealPlanId: $mealPlanId)
        }
    }
`;

export const CanUseRecurringMealPlans = gql`
    query CanUseRecurringMealPlans($siteSetId: InstitutionSetId!) {
        mealPlans {
            canUseRecurring(siteSetId: $siteSetId)
        }
    }
`;

export const ArchiveMealPlan = gql`
    mutation ArchiveMealPlan($mealPlanId: MealPlanId!) {
        mealPlans {
            archive(mealPlanId: $mealPlanId)
        }
    }
`;

export const UpdateMealPlanDays = gql`
    mutation UpdateMealPlanDays($mealPlanId: MealPlanId!, $mealPlanDays: [DayMealPlanInput!]!) {
        mealPlans {
            updateMealPlanDays(mealPlanId: $mealPlanId, mealPlanDays: $mealPlanDays) {
                ...MealPlanScheduleFragment
            }
        }
    }
    ${MealPlanScheduleFragment}
`;

export const DuplicateMealPlan = gql`
    mutation DuplicateMealPlan($mealPlanId: MealPlanId!) {
        mealPlans {
            duplicate(mealPlanId: $mealPlanId)
        }
    }
`;

export const FoodItems = gql`
    query FoodItems($institutionId: InstitutionId!) {
        meals {
            foodItems(institutionId: $institutionId) {
                id
                title
                unit
            }
        }
    }
`;

export const MealTypes = gql`
    query MealTypes($institutionId: InstitutionId!) {
        meals {
            mealTypes(institutionIds: $institutionId) {
                id
                title
                order
                color
            }
        }
    }
`;

export const CreateFoodItem = gql`
    mutation CreateFoodItem($institutionId: InstitutionId!, $title: String!) {
        meals {
            createFoodItem(institutionIds: $institutionId, title: $title) {
                id
                title
            }
        }
    }
`;

export const CreateFoodItems = gql`
    mutation CreateFoodItems($siteSetIds: [SiteSetId!]!, $foodItems: [FoodItemInput!]!) {
        meals {
            createFoodItems(siteSetIds: $siteSetIds, foodItems: $foodItems) {
                id
                title
            }
        }
    }
`;
